'use client';

/**
 * DeepSeekActiveBanner
 *
 * A compact governance warning shown just above the composer whenever the
 * active chat model is a DeepSeek (external) model. Selecting DeepSeek egresses
 * prompt content — including customer PII — to an external provider outside
 * WTC's AWS environment, so the operator should always know when it's live.
 *
 * Renders null for AWS/Claude models. The active provider is the user's explicit
 * pick when set, otherwise the system default.
 */

import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { useModelPreference } from '../../../hooks/useModelPreference';

export interface DeepSeekActiveBannerProps {
  /** Meridian API base — passed through to the preference hook. */
  apiBaseUrl?: string;
}

export function DeepSeekActiveBanner({ apiBaseUrl }: DeepSeekActiveBannerProps) {
  const { selectedModel, systemDefault } = useModelPreference(apiBaseUrl ? { apiBaseUrl } : {});

  const activeProvider = selectedModel ? selectedModel.provider : systemDefault?.provider;
  if (activeProvider !== 'deepseek') return null;

  return (
    <div
      className="mb-1 flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300"
      role="alert"
    >
      <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden />
      <span>
        DeepSeek is active — messages (including customer data) are sent to an external provider
        outside WTC.
      </span>
    </div>
  );
}
